// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Filling BOM slots from a keyboard-wedge scanner (design doc §10.3).
 *
 * A USB or Bluetooth scanner is a keyboard that types very fast and presses Enter. The
 * behaviours §10.3 asks for, and why each one matters on a shop floor:
 *
 * - **the scan lands in the next *empty* slot, skipping filled ones**, so an operator who
 *   re-scans part way through a machine does not overwrite work already done;
 * - **a repeated scan of the same serial is suppressed** — scanners double-fire, and
 *   without this the second shot would be read as "this serial is already used" and
 *   rejected;
 * - **a placeholder never fills a slot**, matching the server (§8.3);
 * - **the remaining count is always visible** and submission stays disabled until it is
 *   zero (US-A-02, US-A-06). The server checks again regardless; this only means the
 *   operator learns about the gap while still holding the scanner rather than after a
 *   round trip.
 *
 * The hook owns the slot map so the same logic serves scanning and typing — §10.3 requires
 * one validation path for both.
 */

import { useCallback, useMemo, useState } from "react";
import { isUsableSerial, normalizeSerial } from "./serials";

export interface Slot {
  positionCode: string;
  positionName: string;
  /** The line's material, whose barcode rules the slot's serial must pass. */
  materialCode?: string | null;
}

/** Whether a serial passes the barcode rules of a slot's material (`barcodeRules.ts`). */
export type SerialCheck = (materialCode: string | null | undefined, serial: string) => boolean;

/**
 * A line of the BOM as the filing screen shows it — every line, not only the ones that
 * need a serial.
 *
 * The screen used to list critical slots alone, which made it a to-do list rather than a
 * picture of the machine: the non-critical lines are filed too, at their whole-machine
 * quantity (§6.2/US-A-02), and leaving them off gave the operator no way to see what the
 * archive was about to record on their behalf.
 */
export interface BomRow extends Slot {
  /** The BOM line's 部位名称 and 图号, on every slot the line expands into. */
  partName: string | null;
  drawingNo: string | null;
  materialCode: string | null;
  /** The BOM line's note for whoever scans it — on every slot the line expands into. */
  remark: string | null;
  isCritical: boolean;
  /** 1 for a serialized slot; the whole-machine quantity for a counted line. */
  quantity: number;
  /** The BOM line the row comes from, that line's parent, and its level — for folding the
   *  table by level. Every slot of one line shares `lineId`. */
  lineId: string | null;
  parentLineId: string | null;
  depth: number;
}

export interface SlotScannerState {
  /** Slot code → what the operator entered, verbatim. */
  values: Record<string, string>;
  /** The slot a scan will fill next, or null when everything is filled. */
  nextSlot: string | null;
  /** The first empty slot after this one in slot order, wrapping round; null when no
   *  other slot is empty. Where a serial typed into a slot's own box moves on to. */
  nextSlotAfter: (positionCode: string) => string | null;
  filledCount: number;
  remainingCount: number;
  isComplete: boolean;
  /** Slot codes still empty, in slot order — for highlighting and scrolling to the first. */
  emptySlots: string[];
  /** Normalized serials entered more than once on this machine. */
  duplicates: string[];
  /** Slot codes holding a serial their material's barcode rules reject. */
  formatMismatches: string[];
  scan: (raw: string) => ScanOutcome;
  setSlot: (positionCode: string, raw: string) => void;
  clear: () => void;
  restore: (values: Record<string, string>) => void;
}

export type ScanOutcome =
  | { kind: "filled"; positionCode: string }
  | { kind: "duplicate"; serial: string }
  | { kind: "format-mismatch"; serial: string; positionCode: string }
  | { kind: "ignored-repeat"; serial: string }
  | { kind: "placeholder" }
  | { kind: "no-empty-slot" };

const ACCEPT_ALL: SerialCheck = () => true;

/**
 * `accepts` applies the materials' barcode rules: a scan its next slot's material rejects
 * is refused, not filled in — the operator is holding the wrong part, or a label nobody
 * has written a rule for yet — and a serial typed into a slot that fails them keeps the
 * filing from completing.
 */
export function useSlotScanner(slots: Slot[], accepts: SerialCheck = ACCEPT_ALL): SlotScannerState {
  const [values, setValues] = useState<Record<string, string>>({});
  const [lastScan, setLastScan] = useState<string | null>(null);

  const derived = useMemo(() => {
    const emptySlots = slots
      .filter((slot) => !isUsableSerial(values[slot.positionCode]))
      .map((slot) => slot.positionCode);

    const seen = new Map<string, number>();
    for (const slot of slots) {
      const normalized = normalizeSerial(values[slot.positionCode]);
      if (!normalized || !isUsableSerial(normalized)) continue;
      seen.set(normalized, (seen.get(normalized) ?? 0) + 1);
    }
    const duplicates = [...seen.entries()].filter(([, count]) => count > 1).map(([key]) => key);
    const formatMismatches = slots
      .filter(
        (slot) =>
          isUsableSerial(values[slot.positionCode]) &&
          !accepts(slot.materialCode, values[slot.positionCode] ?? ""),
      )
      .map((slot) => slot.positionCode);

    return {
      emptySlots,
      duplicates,
      formatMismatches,
      filledCount: slots.length - emptySlots.length,
      remainingCount: emptySlots.length,
      nextSlot: emptySlots[0] ?? null,
      isComplete:
        emptySlots.length === 0 && duplicates.length === 0 && formatMismatches.length === 0,
    };
  }, [slots, values, accepts]);

  const nextSlotAfter = useCallback(
    (positionCode: string): string | null => {
      const empty = new Set(derived.emptySlots);
      empty.delete(positionCode);
      const at = slots.findIndex((slot) => slot.positionCode === positionCode);
      const ordered = [...slots.slice(at + 1), ...slots.slice(0, Math.max(at, 0))];
      return ordered.find((slot) => empty.has(slot.positionCode))?.positionCode ?? null;
    },
    [derived.emptySlots, slots],
  );

  const setSlot = useCallback((positionCode: string, raw: string) => {
    setValues((previous) => ({ ...previous, [positionCode]: raw }));
  }, []);

  const scan = useCallback(
    (raw: string): ScanOutcome => {
      const normalized = normalizeSerial(raw);
      if (!isUsableSerial(normalized)) return { kind: "placeholder" };

      // Scanners double-fire. Without this the second shot reads as a duplicate and the
      // operator is told they made a mistake they did not make.
      if (normalized === lastScan) return { kind: "ignored-repeat", serial: normalized };

      const alreadyUsed = Object.entries(values).some(
        ([, value]) => normalizeSerial(value) === normalized,
      );
      if (alreadyUsed) {
        setLastScan(normalized);
        return { kind: "duplicate", serial: normalized };
      }

      const target = derived.nextSlot;
      if (!target) return { kind: "no-empty-slot" };
      const slot = slots.find((candidate) => candidate.positionCode === target);
      if (!accepts(slot?.materialCode, normalized)) {
        // Remembered as the last scan, so the scanner's double-fire is not reported twice.
        setLastScan(normalized);
        return { kind: "format-mismatch", serial: normalized, positionCode: target };
      }

      setValues((previous) => ({ ...previous, [target]: raw }));
      setLastScan(normalized);
      return { kind: "filled", positionCode: target };
    },
    [derived.nextSlot, lastScan, values, slots, accepts],
  );

  const clear = useCallback(() => {
    setValues({});
    setLastScan(null);
  }, []);

  const restore = useCallback((restored: Record<string, string>) => {
    setValues(restored);
    // Deliberately not restoring `lastScan`: after a reload the operator may well rescan
    // the part they were holding, and suppressing that would look like a dead scanner.
    setLastScan(null);
  }, []);

  return { values, setSlot, scan, clear, restore, nextSlotAfter, ...derived };
}

interface BomItemShape {
  id?: string;
  parent_item_id?: string | null;
  sort_path?: string;
  position_code: string;
  material_info: string;
  /** The name the archive will give the line: `material_info`, qualified when another
   *  line shares it. Older responses lack it, and the description stands in. */
  display_name?: string;
  part_name?: string | null;
  drawing_no?: string | null;
  material_code?: string | null;
  remark?: string | null;
  slot_remarks?: { slot_no: number; remark: string }[];
  is_critical: boolean;
  effective_quantity: number | null;
}

/**
 * Expand a published BOM into the rows the filing screen shows (§6.2.1, §6.2).
 *
 * Critical lines become one row per whole-machine unit, each a slot that needs a serial;
 * this mirrors `build_from_bom` on the server, including the `#n` suffix on the display
 * name — two slots of one line must not both read "关节模组" on a screen where the operator
 * has to tell them apart.
 *
 * Non-critical lines become a single row carrying the quantity, matching the
 * `BOM_QUANTITY` position the server files for them. They are shown but not fillable: the
 * server rejects a serial against a slot code the BOM does not expand to, so an input here
 * would offer the operator a way to fail.
 *
 * Rows keep the BOM's own order, so the screen reads down the machine the way the BOM
 * describes it rather than splitting into "things to scan" and "everything else".
 */
export function expandRows(items: BomItemShape[]): BomRow[] {
  const rows: BomRow[] = [];
  for (const item of items) {
    const quantity = item.effective_quantity ?? 0;
    const name = item.display_name || item.material_info;
    const materialCode = item.material_code ?? null;
    const partName = item.part_name ?? null;
    const drawingNo = item.drawing_no ?? null;
    const remark = item.remark ?? null;
    const tree = {
      lineId: item.id ?? null,
      parentLineId: item.parent_item_id ?? null,
      depth: item.sort_path ? item.sort_path.split(".").length - 1 : 0,
    };
    if (!item.is_critical) {
      rows.push({
        positionCode: item.position_code,
        positionName: name,
        partName,
        drawingNo,
        materialCode,
        remark,
        isCritical: false,
        quantity,
        ...tree,
      });
      continue;
    }
    for (let slotNo = 1; slotNo <= quantity; slotNo += 1) {
      rows.push({
        positionCode: `${item.position_code}#${slotNo}`,
        positionName: `${name}#${slotNo}`,
        partName,
        drawingNo,
        materialCode,
        // The slot's own note if it has one ("关节2"), otherwise what applies to the line.
        remark: item.slot_remarks?.find((entry) => entry.slot_no === slotNo)?.remark ?? remark,
        isCritical: true,
        quantity: 1,
        ...tree,
      });
    }
  }
  return rows;
}

/**
 * The slots a scan can fill: the critical rows, and only those.
 *
 * Derived from `expandRows` rather than walking the items again, so the codes the scanner
 * keys on and the codes the screen displays cannot come apart.
 */
export function expandSlots(items: BomItemShape[]): Slot[] {
  return expandRows(items)
    .filter((row) => row.isCritical)
    .map(({ positionCode, positionName, materialCode }) => ({
      positionCode,
      positionName,
      materialCode,
    }));
}
